import { createLogger, createStore } from "vuex";
import createPersistedState from "vuex-persistedstate";
import {
  FETCH_CATEGORIES,
  FETCH_PRODUCTS,
  FETCH_SWEDISH_SELLER,
  FETCH_TEOLLISUUS_SELLER,
  SET_ADDRESS,
  SET_CATEGORIES,
  SET_FACTORY,
  SET_FILTER_CATEGORY,
  SET_FILTERED_PRODUCTS,
  SET_PRODUCTS,
  SET_SELECTED_AMOUNTS,
  SET_SELLER,
  SET_TEOLLISUUS_SELLER,
  UPDATE_DISTANCE,
  UPDATE_ECO_FILTER,
  UPDATE_FILTER_CATEGORY,
  UPDATE_FILTER_URL,
  UPDATE_PROFILE,
  UPDATE_WINTER_FILTER,
} from "./constants";
import {
  buildCategoryStructure,
  createStructure,
  getDirectionDistance,
  isInRange,
  retrieveSeller,
  retrieveSwedishSeller,
  retrieveTeollisuusSellers,
} from "../shared/helpers";
import axios from "axios";
import * as _debug from "debug";
const debug = _debug("soilfood:store");

const persistenceOptions = {
  key: "soilfood_store_v2",
  storage: window.sessionStorage,
  paths: ["address_info", "seller", "product_filters", "selectedAmounts"],
};

export default createStore({
  state: {
    address_info: {}, // Ignore the inconsistent naming..
    seller: [],
    industry_sellers: [],
    products: [],
    categories: {},
    product_filters: {
      category: false,
      eco: false,
      winter: false,
    },
    filteredProducts: {}, // Ignore the inconsistent naming..
    selectedAmounts: {}, // Ignore the inconsistent naming..
    is_industry: false,
  },
  mutations: {
    [SET_ADDRESS](state, payload) {
      state.address_info = { ...state.address_info, ...payload };
    },
    [SET_SELLER](state, payload) {
      if (!Array.isArray(payload)) {
        payload = [payload];
      }
      state.seller = [...payload];
    },
    [SET_FILTERED_PRODUCTS](state, payload) {
      state.filteredProducts = { ...payload };
    },
    [SET_SELECTED_AMOUNTS](state, payload) {
      state.selectedAmounts = { ...state.selectedAmounts, ...payload };
    },
    [SET_PRODUCTS](state, payload) {
      state.products = [...payload];
    },
    [SET_TEOLLISUUS_SELLER](state, payload) {
      state.industry_sellers = [...payload];
    },
    [SET_CATEGORIES](state, payload) {
      state.categories = { ...payload };
    },
    [SET_FACTORY](state, payload) {
      state.is_industry = payload;
    },
    [SET_FILTER_CATEGORY](state, payload) {
      let update;
      if (typeof payload === "string") {
        let found = Object.values(state.categories).filter(
          (c) => c.slug === payload
        );
        if (found.length > 0) {
          update = found[0].id;
          state.product_filters = {
            ...state.product_filters,
            category: update,
          };
          return;
        } else return;
      }
      if (state.product_filters.category === payload) payload = false;
      state.product_filters = { ...state.product_filters, category: payload };
    },
    [UPDATE_WINTER_FILTER](state, payload) {
      if (!payload)
        state.product_filters = {
          ...state.product_filters,
          winter: !state.product_filters.winter,
        };
      else
        state.product_filters = { ...state.product_filters, winter: payload };
    },
    [UPDATE_ECO_FILTER](state, payload) {
      if (!payload)
        state.product_filters = {
          ...state.product_filters,
          eco: !state.product_filters.eco,
        };
      else state.product_filters = { ...state.product_filters, eco: payload };
    },
  },
  actions: {
    async [UPDATE_PROFILE]({ commit, dispatch }, payload) {
      const { placeObject, ignoreSellerError } = payload;
      const data = await retrieveSeller(placeObject);

      if (!data && !ignoreSellerError) return false;

      commit(SET_ADDRESS, placeObject);
      commit(SET_SELLER, data);

      await dispatch(UPDATE_DISTANCE);
      return true;
    },
    // eslint-disable-next-line
    async [FETCH_PRODUCTS]({ commit, state, dispatch }, payload) {
      if (state.products.length > 0 && !payload) return;
      const response = await axios.get(
        process.env.NODE_ENV === "production"
          ? "/wp-json/wp/v2/tuotteet/?per_page=100&_embed"
          : "https://soilfood.localhost/wp-json/wp/v2/tuotteet/?per_page=100&_embed"
      );
      const products = [];
      if (response.status === 200) {
        response.data.forEach((product) => {
          Object.defineProperty(product.acf, "title", {
            value: product.title.rendered,
            writable: true,
            enumerable: true,
            configurable: true,
          });

          // Set virtual category for rakennekalkit with number
          /* if (product.tuotekategoria.includes(152)) {
            let prodnums = product.title.rendered.split(" ");
            if (parseInt(prodnums[prodnums.length - 1])) {
               product.tuotekategoria[1] = 1520;
            }
          }*/
          products.push(product);
        });
      }
      commit(SET_PRODUCTS, products);
      await dispatch(UPDATE_DISTANCE);
    },
    // eslint-disable-next-line
    async [FETCH_CATEGORIES]({ commit, state }, payload) {
      if (Object.keys(state.categories).length > 0 && !payload) return;
      const response = await axios.get(
        process.env.NODE_ENV === "production"
          ? "/wp-json/wp/v2/tuotekategoria?per_page=100"
          : "https://soilfood.localhost/wp-json/wp/v2/tuotekategoria?per_page=100"
      );

      if (response.status === 200) {
        const categories = buildCategoryStructure(response.data);
        commit(SET_CATEGORIES, categories);
      }
    },
    async [UPDATE_DISTANCE]({ commit, state }) {
      if (state.products.length < 1) return;
      const newProducts = await Promise.all(
        state.products.map(async (product) => {
          let geometryLocation;
          debug(`location: ${state.address_info.geometryLocation}`);
          if (state.address_info.geometryLocation) {
            geometryLocation = state.address_info.geometryLocation;
          } else {
            product.distance = false;
            return product;
          }
          debug(product);
          if (!product.acf.geopoint) {
            product.distance = false;
            return product;
          }
          const locArray = product.acf.geopoint.split(",");
          const loc = { lat: locArray[0], lng: locArray[1] };
          const range = parseInt(product.acf.toimitusetaisyys);
          const calculated = isInRange(geometryLocation, loc, range);
          // Only calculate directions for distances over > 100
          debug(`Checking: ${calculated} ${range / 1.4}`);
          if (calculated && calculated > range / 1.4) {
            product.distance = await getDirectionDistance(
              geometryLocation,
              loc,
              range
            );
          } else {
            product.distance = calculated;
          }
          debug(product.distance);
          return product;
        })
      );

      commit(SET_PRODUCTS, newProducts);
    },
    async [FETCH_SWEDISH_SELLER]({ commit }) {
      const data = await retrieveSwedishSeller();
      if (!data) return false;
      commit(SET_SELLER, data);
    },
    async [FETCH_TEOLLISUUS_SELLER]({ commit }) {
      const data = await retrieveTeollisuusSellers();
      if (!data) return false;
      commit(SET_TEOLLISUUS_SELLER, data);
    },
    async [UPDATE_FILTER_CATEGORY]({ commit, dispatch }, payload) {
      commit(SET_FILTER_CATEGORY, payload);
      await dispatch(UPDATE_FILTER_URL);
    },
    async [UPDATE_FILTER_URL]({ state }) {
      let slug;
      if (state.product_filters.category) {
        if (typeof state.product_filters.category === "string") {
          let found = Object.values(state.categories).filter(
            (c) => c.slug === state.product_filters.category
          );
          if (found.length > 0) {
            slug = found[0].slug;
          } else return;
        }
        if (!slug) {
          let found = Object.values(state.categories).filter(
            (c) => c.id === state.product_filters.category
          );
          if (found.length > 0) {
            slug = found[0].slug;
          } else return;
        }
        if (!slug) return;
        let url = new URL(window.location.href);
        url.pathname = `/tuotekategoria/${slug}`;
        window.history.pushState({}, "", url);
      } else {
        let url = new URL(window.location.href);
        url.pathname = "/maatalous/tuotteet";
        window.history.pushState({}, "", url);
      }
    },
  },
  getters: {
    getSeller(state) {
      if (state.is_industry) return state.industry_sellers;
      if (Object.keys(state.seller).length === 0) return false;
      if (!Array.isArray(state.seller)) return [state.seller];
      return state.seller;
    },
    getFactory(state) {
      return state.is_industry;
    },
    getPhDelta(state) {
      let target = state.selectedAmounts.phTarget;
      let start = state.selectedAmounts.phStart;
      if (isNaN(target)) {
        target = state.selectedAmounts.phTarget.replace(",", ".");
        target = parseFloat(target);
      }
      if (isNaN(start)) {
        start = state.selectedAmounts.phStart.replace(",", ".");
        start = parseFloat(start);
      }
      return target - start;
    },
    getMaalaji(state) {
      if (state.selectedAmounts.selectedMaalaji) {
        if (
          state.selectedAmounts.selectedMaalaji.length > 1 &&
          state.selectedAmounts.selectedMultavuus
        ) {
          return state.selectedAmounts.selectedMaalaji[
            state.selectedAmounts.selectedMultavuus
          ];
        } else {
          return state.selectedAmounts.selectedMaalaji[0];
        }
      }
      return null;
    },
    getKalkkilaskuriProducts(state) {
      debug(`Kalkkilaskuri filters: ${state.filteredProducts}`);
      return Object.keys(state.filteredProducts).length !== 0
        ? state.filteredProducts
        : false;
    },
    getSortedProducts(state) {
      const orderArray = [
        "special",
        "rakenne",
        "rakenne-extra",
        "teho",
        "teho-extra",
        "maanparannus",
        "ravinne",
      ];
      const productCategories = Object.keys(state.filteredProducts);
      debug(`prodcat ${productCategories}`);
      return orderArray.filter((o) => productCategories.includes(o));
    },
    getFilteredProducts(state) {
      console.time("filterProducts");

      // Initial filter and filter by location.
      let filteredProducts = state.products.filter((product) => {
        if (product.slug === "muu-tuote") return false;
        let geometryLocation;
        debug(`location: ${state.address_info.geometryLocation}`);
        if (state.address_info.geometryLocation) {
          geometryLocation = state.address_info.geometryLocation;
        } else return true;
        debug(product);
        if (!product.acf.geopoint) return false;
        const locArray = product.acf.geopoint.split(",");
        const loc = { lat: locArray[0], lng: locArray[1] };
        const range = parseInt(product.acf.toimitusetaisyys);
        product.distance = isInRange(geometryLocation, loc, range);
        debug(product.distance);
        return product.distance !== false;
      });
      filteredProducts = filteredProducts.filter((p) => {
        if (state.product_filters.eco) {
          return p.acf.luomu;
        } else return true;
      });
      filteredProducts = filteredProducts.filter((p) => {
        if (state.product_filters.winter) {
          return p.acf.talvi_kalkitukseen;
        } else return true;
      });
      return createStructure(filteredProducts, state.product_filters.category);
    },
    getAddressLine(state) {
      return state.address_info.addressLine
        ? state.address_info.addressLine
        : "";
    },
    getKalkit(state) {
      return state.products.filter(
        (p) => p.tuotekategoria.includes(147) && !p.acf.luomu
      );
    },
    getFilterCategory(state) {
      return state.product_filters.category;
    },
    getCategory: (state) => (key) => {
      return state.categories[key];
    },
    getProducts(state) {
      return state.products;
    },
  },
  modules: {},
  plugins:
    process.env.NODE_ENV !== "production"
      ? [createLogger(), createPersistedState(persistenceOptions)]
      : [createPersistedState(persistenceOptions)],
});
